// GLIMState -- GLIM expressing a REAL product state (see productStates.js).
//
// * `state` is one of the 15 approved states; unknown values fall back to
//   "neutral" -- there is no random cycling and no new expression.
// * Critical states (error, warning, approval required, acting, something
//   changed) always render a visible text label next to the face, so the
//   state is never understood through the character alone. Pass
//   `showLabel={false}` only when the surrounding UI already states it.
// * Motion is calm and mostly one-shot; it is disabled under
//   prefers-reduced-motion (brand.css). The state PNG itself carries the
//   expression, so the settled frame is always complete.

import { GLIM_STATE_FILES } from './assets';
import { glimStateMeta, isGlimState } from './productStates';
import './brand.css';

const SIGNAL_DOT_STATES = new Set(['approval_required', 'warning', 'error', 'something_changed']);

export function GLIMState({
  state = 'neutral',
  size = 72,
  showLabel,
  label,
  description,
  showDescription = false,
  layout = 'inline',
  tone = 'light',
  motion = true,
  announce = true,
  className = '',
}) {
  const id = isGlimState(state) ? state : 'neutral';
  const meta = glimStateMeta(id);
  const text = label || meta.label;
  const visibleLabel = showLabel === undefined ? meta.requiresText : showLabel;
  const motionClass = motion ? `glim-motion-${meta.motion}` : '';

  return (
    <span
      className={[
        'glim-state',
        layout === 'stacked' ? 'glim-state--stacked' : '',
        tone === 'dark' ? 'glim-state--dark' : '',
        className,
      ].filter(Boolean).join(' ')}
      data-testid="glim-state"
      data-state={id}
      role={announce ? 'status' : undefined}
    >
      <img
        key={id}
        src={GLIM_STATE_FILES[id]}
        width={size}
        height={size}
        alt=""
        aria-hidden="true"
        draggable={false}
        className={`glim ${motionClass}`.trim()}
      />
      {visibleLabel ? (
        <span>
          <span className="glim-state__label" data-testid="glim-state-label">
            {SIGNAL_DOT_STATES.has(id) && <span className="glim-state__signal-dot" aria-hidden="true" />}
            {text}
          </span>
          {showDescription && (
            <span className="glim-state__description" style={{ display: 'block' }}>
              {description || meta.description}
            </span>
          )}
        </span>
      ) : (
        <span className="sr-only" aria-hidden={announce ? undefined : 'true'}>{description || meta.description}</span>
      )}
    </span>
  );
}

export default GLIMState;
